'use client';

import { c as _c } from "react/compiler-runtime";
import type { OnrampClient } from '@morfi/onramp-sdk';
import { type ContactVerification, useContactVerification } from '@morfi/onramp-sdk/react';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { InputField } from '@/components/ui/input-field';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { TOAST_IDS } from '@/config/toast-ids';
import { showToast } from '@/lib/ui/toast';
import { useFundingStage } from '../shared/funding-chrome';
export function FundingVerification(t0) {
  const $ = _c(13);
  const {
    client,
    forceConsent: t1,
    sessionError: t2,
    onContinue,
    onBack,
    country
  } = t0;
  const forceConsent = t1 === undefined ? false : t1;
  const sessionError = t2 === undefined ? false : t2;
  const verification = useContactVerification(client, forceConsent);
  const continued = useRef(false);
  let t3;
  let t4;
  if ($[0] !== onContinue || $[1] !== verification.step) {
    t3 = () => {
      if (verification.step === "ready" && !continued.current) {
        continued.current = true;
        onContinue();
      }
    };
    t4 = [verification.step, onContinue];
    $[0] = onContinue;
    $[1] = verification.step;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  const t5 = `${verification.kind}:${verification.step}`;
  let t6;
  if ($[4] !== onContinue || $[5] !== sessionError || $[6] !== verification) {
    t6 = sessionError && verification.step === "ready" ? {
      ...verification,
      step: "unavailable",
      error: "verification_failed",
      retry: onContinue
    } : verification;
    $[4] = onContinue;
    $[5] = sessionError;
    $[6] = verification;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== country || $[9] !== onBack || $[10] !== t5 || $[11] !== t6) {
    t7 = <VerificationForm key={t5} verification={t6} onBack={onBack} country={country} />;
    $[8] = country;
    $[9] = onBack;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
export function VerificationForm(t0) {
  const $ = _c(105);
  const {
    verification: v,
    requireConsent: t1,
    onBack,
    country
  } = t0;
  const requireConsent = t1 === undefined ? true : t1;
  const t = useTranslations("onramp");
  const onboarding = useTranslations("onboarding");
  const [codeValue, setCodeValue] = useState("");
  const value = v.step === "code" ? codeValue : v.draft;
  const setValue = v.step === "code" ? setCodeValue : v.setDraft;
  const transientError = !v.busy && !v.loading && (v.step === "email" || v.step === "phone" || v.step === "code" || v.step === "consent") ? v.error : null;
  const announcedError = useRef(null);
  let t2;
  let t3;
  if ($[0] !== t || $[1] !== transientError) {
    t2 = () => {
      if (transientError && announcedError.current !== transientError) {
        const message = transientError === "code_invalid" ? t("code_invalid") : transientError === "verification_delivery_failed" ? t("verification_delivery_failed") : transientError === "verification_rate_limited" ? t("verification_rate_limited") : transientError === "sign_in" ? t("sign_in") : t("verification_failed");
        showToast({
          id: TOAST_IDS.ONRAMP_VERIFICATION_ERROR,
          message,
          variant: "error"
        });
      }
      announcedError.current = transientError;
    };
    t3 = [transientError, t];
    $[0] = t;
    $[1] = transientError;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== t || $[5] !== v.kind || $[6] !== v.step) {
    t4 = v.step === "consent" ? t("contacts_verified") : v.kind === "email" ? t("verify_email") : t("verify_phone");
    $[4] = t;
    $[5] = v.kind;
    $[6] = v.step;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onBack || $[9] !== v) {
    t5 = onBack || v.step === "code" ? () => {
      if (!v.busy && !v.back()) {
        onBack?.();
      }
    } : undefined;
    $[8] = onBack;
    $[9] = v;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useFundingStage(t4, false, t5);
  if (v.loading) {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Skeleton className="h-24 w-full rounded-control" />;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (v.step === "unavailable" || v.step === "refresh" && v.error) {
    let t6;
    if ($[12] !== t) {
      t6 = t("verification_failed");
      $[12] = t;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t6) {
      t7 = <p>{t6}</p>;
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    const t8 = v.retry;
    let t9;
    if ($[16] !== t) {
      t9 = t("retry");
      $[16] = t;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== t9 || $[19] !== v.retry) {
      t10 = <AnimatedButton onClick={t8}>{t9}</AnimatedButton>;
      $[18] = t9;
      $[19] = v.retry;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== onBack || $[22] !== t) {
      t11 = onBack && <AnimatedButton variant="tertiary" onClick={onBack}>{t("back")}</AnimatedButton>;
      $[21] = onBack;
      $[22] = t;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== t10 || $[25] !== t11 || $[26] !== t7) {
      t12 = <div role="alert" className="flex flex-col gap-3">{t7}{t10}{t11}</div>;
      $[24] = t10;
      $[25] = t11;
      $[26] = t7;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    return t12;
  }
  if (v.step === "ready" || v.step === "refresh" && !v.error) {
    let t6;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Skeleton className="h-24 w-full rounded-control" />;
      $[28] = t6;
    } else {
      t6 = $[28];
    }
    return t6;
  }
  if (v.step === "consent" && requireConsent && !v.consent) {
    let t6;
    if ($[29] !== t) {
      t6 = t("verified_terms_unavailable");
      $[29] = t;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    let t7;
    if ($[31] !== t6) {
      t7 = <p className="text-caption text-foreground-secondary">{t6}</p>;
      $[31] = t6;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    const t8 = v.retry;
    let t9;
    if ($[33] !== t) {
      t9 = t("retry");
      $[33] = t;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] !== t9 || $[36] !== v.retry) {
      t10 = <AnimatedButton onClick={t8}>{t9}</AnimatedButton>;
      $[35] = t9;
      $[36] = v.retry;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    let t11;
    if ($[38] !== t10 || $[39] !== t7) {
      t11 = <div role="alert" className="flex flex-col gap-3">{t7}{t10}</div>;
      $[38] = t10;
      $[39] = t7;
      $[40] = t11;
    } else {
      t11 = $[40];
    }
    return t11;
  }
  const code = v.step === "code";
  const email = v.kind === "email";
  const confirmed = v.step === "consent";
  const inlineConsent = requireConsent && Boolean(v.consent) && (confirmed || code && !email);
  let t6;
  if ($[41] !== code || $[42] !== confirmed || $[43] !== email || $[44] !== value) {
    t6 = confirmed || (code ? /^\d{6}$/.test(value) : email ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) : /^\+[1-9]\d{7,14}$/.test(value.replace(/[\s()-]/g, "")));
    $[41] = code;
    $[42] = confirmed;
    $[43] = email;
    $[44] = value;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  const valid = t6;
  let t7;
  if ($[46] !== code || $[47] !== confirmed || $[48] !== inlineConsent || $[49] !== v || $[50] !== valid || $[51] !== value) {
    t7 = function submit() {
      if (!valid || v.busy || inlineConsent && !v.consent) {
        return;
      }
      confirmed ? v.accept() : code ? v.check(value, inlineConsent ? v.consent?.version : undefined) : v.send(value);
    };
    $[46] = code;
    $[47] = confirmed;
    $[48] = inlineConsent;
    $[49] = v;
    $[50] = valid;
    $[51] = value;
    $[52] = t7;
  } else {
    t7 = $[52];
  }
  const submit = t7;
  let t8;
  if ($[53] !== submit) {
    t8 = event => {
      event.preventDefault();
      submit();
    };
    $[53] = submit;
    $[54] = t8;
  } else {
    t8 = $[54];
  }
  let t9;
  if ($[55] !== email || $[56] !== t || $[57] !== v.busy || $[58] !== v.changeEmail || $[59] !== v.verifiedEmail) {
    t9 = !email && v.verifiedEmail && <div className="flex items-center justify-between gap-3 rounded-control bg-background-secondary px-3 py-2"><div className="min-w-0"><p className="text-caption text-foreground-secondary">{t("verified_email")}</p><p className="break-all text-caption">{v.verifiedEmail}</p></div><button type="button" className="shrink-0 text-caption underline underline-offset-4 disabled:opacity-50" disabled={v.busy} onClick={v.changeEmail}>{t("change_email")}</button></div>;
    $[55] = email;
    $[56] = t;
    $[57] = v.busy;
    $[58] = v.changeEmail;
    $[59] = v.verifiedEmail;
    $[60] = t9;
  } else {
    t9 = $[60];
  }
  let t10;
  if ($[61] !== code || $[62] !== email || $[63] !== t) {
    t10 = code && <p className="text-caption">{email ? t("email_code_sent") : t("phone_code_sent")}</p>;
    $[61] = code;
    $[62] = email;
    $[63] = t;
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  let t11;
  if ($[65] !== code || $[66] !== confirmed || $[67] !== country || $[68] !== email || $[69] !== onboarding || $[70] !== setValue || $[71] !== t || $[72] !== v.busy || $[73] !== v.error || $[74] !== value) {
    t11 = !confirmed && !code && !email ? <div className="flex flex-col gap-1.5"><Label className="text-caption leading-5">{t("phone_number")}</Label><PhoneInput value={value} onChange={setValue} defaultCountry={country ?? "US"} placeholder={t("phone_number")} countryLabel={t("kyc_choose_country")} searchPlaceholder={t("kyc_search_country")} noResultsLabel={onboarding("phoneNoResults")} disabled={v.busy} invalid={v.error === "code_invalid"} size="lg" className="h-11 bg-background-secondary" /></div> : !confirmed ? <InputField className="bg-background-secondary dark:bg-background-secondary" containerClassName="gap-1.5 [&_input]:h-11" labelClassName="text-caption leading-5" label={code ? t("verification_code") : email ? t("email_address") : t("phone_number")} type={code ? "text" : email ? "email" : "tel"} inputMode={code ? "numeric" : email ? "email" : "tel"} autoComplete={code ? "one-time-code" : email ? "email" : "tel"} value={value} maxLength={code ? 6 : email ? 254 : 24} onChange={event_0 => setValue(code ? event_0.target.value.replace(/\D/g, "").slice(0, 6) : event_0.target.value)} disabled={v.busy} aria-invalid={v.error === "code_invalid"} /> : null;
    $[65] = code;
    $[66] = confirmed;
    $[67] = country;
    $[68] = email;
    $[69] = onboarding;
    $[70] = setValue;
    $[71] = t;
    $[72] = v.busy;
    $[73] = v.error;
    $[74] = value;
    $[75] = t11;
  } else {
    t11 = $[75];
  }
  let t12;
  if ($[76] !== inlineConsent || $[77] !== v) {
    t12 = inlineConsent && <InlineConsent verification={v} />;
    $[76] = inlineConsent;
    $[77] = v;
    $[78] = t12;
  } else {
    t12 = $[78];
  }
  const t13 = !valid || v.busy || !code && !confirmed && v.cooldown || inlineConsent && !v.consent;
  let t14;
  if ($[79] !== code || $[80] !== confirmed || $[81] !== t) {
    t14 = confirmed || code ? t("verify_continue") : t("send_code");
    $[79] = code;
    $[80] = confirmed;
    $[81] = t;
    $[82] = t14;
  } else {
    t14 = $[82];
  }
  let t15;
  if ($[83] !== t13 || $[84] !== t14 || $[85] !== v.busy) {
    t15 = <AnimatedButton size="lg" loading={v.busy} className="h-11 rounded-control" type="submit" disabled={t13}>{t14}</AnimatedButton>;
    $[83] = t13;
    $[84] = t14;
    $[85] = v.busy;
    $[86] = t15;
  } else {
    t15 = $[86];
  }
  let t16;
  if ($[87] !== code || $[88] !== email || $[89] !== t || $[90] !== v) {
    t16 = code && <div className="grid grid-cols-2 gap-2"><AnimatedButton type="button" variant="outline" size="lg" className="h-10.5 rounded-control" disabled={v.busy || v.cooldown || !v.draft} onClick={() => {
        v.send(v.draft);
      }}>{v.cooldown ? t("resend_in_seconds", {
          seconds: v.resendInSeconds
        }) : t("resend_code")}</AnimatedButton><AnimatedButton type="button" variant="outline" size="lg" className="h-10.5 rounded-control" disabled={v.busy} onClick={v.edit}>{email ? t("change_email") : t("change_phone")}</AnimatedButton></div>;
    $[87] = code;
    $[88] = email;
    $[89] = t;
    $[90] = v;
    $[91] = t16;
  } else {
    t16 = $[91];
  }
  let t17;
  if ($[92] !== code || $[93] !== t || $[94] !== v.cooldown) {
    t17 = !code && v.cooldown && <p className="text-caption text-foreground-secondary">{t("resend_wait")}</p>;
    $[92] = code;
    $[93] = t;
    $[94] = v.cooldown;
    $[95] = t17;
  } else {
    t17 = $[95];
  }
  let t18;
  if ($[96] !== t10 || $[97] !== t11 || $[98] !== t12 || $[99] !== t15 || $[100] !== t16 || $[101] !== t17 || $[102] !== t8 || $[103] !== t9) {
    t18 = <form className="flex flex-col gap-5" onSubmit={t8} data-private="true">{t9}{t10}{t11}{t12}{t15}{t16}{t17}</form>;
    $[96] = t10;
    $[97] = t11;
    $[98] = t12;
    $[99] = t15;
    $[100] = t16;
    $[101] = t17;
    $[102] = t8;
    $[103] = t9;
    $[104] = t18;
  } else {
    t18 = $[104];
  }
  return t18;
}
function InlineConsent(t0) {
  const $ = _c(22);
  const {
    verification: v
  } = t0;
  const t = useTranslations("onramp");
  if (!v.consent) {
    let t1;
    if ($[0] !== t) {
      t1 = t("consent_unavailable");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <p role="alert">{t1}</p>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== v.consent.text) {
    t1 = <p>{v.consent.text}</p>;
    $[4] = v.consent.text;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = v.consent.termsUrl;
  let t3;
  if ($[6] !== t) {
    t3 = t("moonpay_terms");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3 || $[9] !== v.consent.termsUrl) {
    t4 = <a className="underline" href={t2} target="_blank" rel="noopener noreferrer">{t3}</a>;
    $[8] = t3;
    $[9] = v.consent.termsUrl;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = v.consent.privacyUrl;
  let t6;
  if ($[11] !== t) {
    t6 = t("privacy_policy");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6 || $[14] !== v.consent.privacyUrl) {
    t7 = <a className="underline" href={t5} target="_blank" rel="noopener noreferrer">{t6}</a>;
    $[13] = t6;
    $[14] = v.consent.privacyUrl;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t4 || $[17] !== t7) {
    t8 = <p>{t4}{" \xB7 "}{t7}</p>;
    $[16] = t4;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t1 || $[20] !== t8) {
    t9 = <div className="flex flex-col gap-2 text-caption text-foreground-secondary">{t1}{t8}</div>;
    $[19] = t1;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}